import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, Camera, Search, AlertTriangle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { getTodayTasks } from '../lib/api';

export default function MobileHomePage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState({ deliveries: [], collections: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await getTodayTasks();
        setTasks(response.data);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, []);

  const actionButtons = [
    { 
      path: '/scanner/delivery', 
      icon: Truck, 
      label: 'Scan for Delivery',
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    { 
      path: '/scanner/collection', 
      icon: Package, 
      label: 'Scan for Collection',
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    { 
      path: '/scanner/damage', 
      icon: Camera, 
      label: 'Report Damage',
      color: 'text-red-600',
      bg: 'bg-red-50'
    },
    { 
      path: '/scanner/find', 
      icon: Search, 
      label: 'Find Asset',
      color: 'text-amber-600',
      bg: 'bg-amber-50'
    },
  ];

  return (
    <div className="max-w-lg mx-auto space-y-6" data-testid="mobile-home-page">
      {/* Welcome */}
      <div className="bg-secondary text-white p-6 -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8">
        <h1 className="font-heading font-black text-2xl">
          Welcome, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="font-body text-slate-300 mt-1">
          {new Date().toLocaleDateString('en-GB', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long' 
          })}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4">
        {actionButtons.map((action) => (
          <Link 
            key={action.path} 
            to={action.path}
            data-testid={`action-${action.label.toLowerCase().replace(/ /g, '-')}`}
          >
            <div className="mobile-action-btn">
              <div className={`w-14 h-14 ${action.bg} rounded-full flex items-center justify-center mb-3`}>
                <action.icon className={`w-7 h-7 ${action.color}`} />
              </div>
              <span className="mobile-action-btn-label text-center">{action.label}</span>
            </div>
          </Link>
        ))}
      </div>

      {/* Today's Tasks */}
      <Card className="border-2 border-slate-200 shadow-industrial">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Today's Tasks
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Deliveries */}
              {tasks.deliveries.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                    <p className="font-mono text-xs uppercase text-blue-600 font-bold">
                      Pending Deliveries ({tasks.deliveries.length})
                    </p>
                  </div>
                  {tasks.deliveries.map((task, index) => (
                    <Link
                      key={index}
                      to={`/scanner/delivery`}
                      className="flex items-center gap-3 p-4 border-b border-slate-100 hover:bg-slate-50"
                    >
                      <Truck className="w-5 h-5 text-blue-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-body font-semibold text-sm truncate">{task.client}</p>
                        <p className="font-mono text-xs text-slate-500">{task.order_number}</p>
                      </div>
                      <span className="badge-info">{task.items} items</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* Collections */}
              {tasks.collections.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-green-50 border-b border-green-100">
                    <p className="font-mono text-xs uppercase text-green-600 font-bold">
                      Pending Collections ({tasks.collections.length})
                    </p>
                  </div>
                  {tasks.collections.map((task, index) => (
                    <Link
                      key={index}
                      to={`/scanner/collection`}
                      className="flex items-center gap-3 p-4 border-b border-slate-100 hover:bg-slate-50"
                    >
                      <Package className="w-5 h-5 text-green-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-body font-semibold text-sm truncate">{task.client}</p>
                        <p className="font-mono text-xs text-slate-500">{task.order_number}</p>
                      </div>
                      <span className="badge-success">{task.items} items</span>
                    </Link>
                  ))}
                </div>
              )}

              {tasks.deliveries.length === 0 && tasks.collections.length === 0 && (
                <div className="p-8 text-center">
                  <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="font-body text-slate-400">No tasks for today</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
